// RollsOrder.js

import {
  Col,
  Row,
  Space,
  Table,
  Input,
  Button,
  InputNumber,
  message,
  Badge,
  Tag,
  Typography,
} from "antd"
import {
  CopyrightCircleOutlined,
  DeleteOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CarOutlined,
} from "@ant-design/icons"
import { useEffect, useState, useRef, useMemo } from "react"
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
  getDocs,
  writeBatch,
  doc,
  serverTimestamp,
} from "firebase/firestore"
import PageHeader from "../../../Elements/PageHeader"
import { db } from "../../../../firebase/config"
import OrderBucketDrawer from "./OrderBucketDrawer"
import { useCollection } from "../../../../hooks/useCollection"
// 1. Import useCollection to fetch machines

export default function RollsOrder() {
  const [rollsLoading, setRollsLoading] = useState(true)
  const [rollsData, setRollsData] = useState([])
  const searchInputRef = useRef(null)

  const [bucketItems, setBucketItems] = useState([])
  const [isBucketVisible, setIsBucketVisible] = useState(false)
  const [isPlacingOrder, setIsPlacingOrder] = useState(false)

  // 2. Fetch machines data
  const { data: machinesData, loading: machinesLoading } = useCollection("machines")

  const bucketTotalSum = useMemo(() => {
    return bucketItems.reduce((sum, item) => sum + (item.price || 0), 0)
  }, [bucketItems])

  useEffect(() => {
    setRollsLoading(true)
    const rollRef = collection(db, "rollStore")

    const unsubscribe = onSnapshot(
      rollRef,
      (snapshot) => {
        let results = []
        snapshot.docs.forEach((doc) => {
          results.push({ ...doc.data(), id: doc.id })
        })
        setRollsData(results)
        setRollsLoading(false)
      },
      (error) => {
        console.error("Error fetching rolls: ", error)
        message.error("Failed to load available rolls.")
        setRollsLoading(false)
      },
    )
    return () => unsubscribe()
  }, [])

  const handleAddToBucket = (rollToAdd) => {
    if (rollToAdd.inOrder) {
      message.warning(`Roll ${rollToAdd.barcode || rollToAdd.id} is already part of another order.`)
      return
    }
    if (!rollToAdd.verified) {
      message.warning(`Roll ${rollToAdd.barcode || rollToAdd.id} has not arrived in stock yet.`)
      return
    }

    if (!bucketItems.find((item) => item.id === rollToAdd.id)) {
      setBucketItems((prevItems) => [...prevItems, rollToAdd])
      message.success(`Roll ${rollToAdd.barcode || rollToAdd.id} added to order.`)
    } else {
      message.info(`Roll ${rollToAdd.barcode || rollToAdd.id} is already in the order.`)
    }
  }

  const handleRemoveFromBucket = (rollIdToRemove) => {
    setBucketItems((prevItems) => prevItems.filter((item) => item.id !== rollIdToRemove))
    message.info("Roll removed from order.")
  }

  // 3. Update handlePlaceOrder to accept the selected machine
  const handlePlaceOrder = async (destinationMachine) => {
    if (bucketItems.length === 0) {
      message.warning("Your order bucket is empty.")
      return
    }
    // Add check for destination machine
    if (!destinationMachine) {
      message.error("Please select a destination machine.")
      return
    }
    setIsPlacingOrder(true)

    const unavailableItems = []
    const currentRollsMap = new Map(rollsData.map((r) => [r.id, r]))

    for (const item of bucketItems) {
      const currentRollState = currentRollsMap.get(item.id)
      if (!currentRollState || currentRollState.inOrder || !currentRollState.verified) {
        unavailableItems.push(item.barcode || item.id)
      }
    }

    if (unavailableItems.length > 0) {
      message.error(
        `Some items are no longer available: ${unavailableItems.join(
          ", ",
        )}. Please review your bucket.`,
      )
      setIsPlacingOrder(false)
      return
    }

    try {
      const ordersQuery = query(
        collection(db, "rollOrders"),
        orderBy("orderNumber", "desc"),
        limit(1),
      )
      const querySnapshot = await getDocs(ordersQuery)
      let nextOrderNumber = 1
      if (!querySnapshot.empty) {
        const lastOrder = querySnapshot.docs[0].data()
        nextOrderNumber = (lastOrder.orderNumber || 0) + 1
      }

      const newOrderRef = doc(collection(db, "rollOrders"))
      const orderItems = bucketItems.map((item) => ({
        rollId: item.id,
        barcode: item.barcode,
        material: item.material?.material || "N/A",
        materialId: item.materialId,
        originalMeters: item.originalMeters,
        remainingMeters: item.remainingMeters,
        rollWidth: item.rollWidth,
        price: item.price || 0,
      }))

      const totalAmount = bucketTotalSum

      const fullOrderNumber = `RO${nextOrderNumber}`

      const orderData = {
        orderNumber: nextOrderNumber,
        fullOrderNumber: fullOrderNumber,
        orderId: newOrderRef.id,
        createdAt: serverTimestamp(),
        status: "Pending Payment",
        items: orderItems,
        totalAmount: totalAmount,
        itemCount: bucketItems.length,
        destinationMachine: destinationMachine, // Add machine to the order summary
      }

      const batch = writeBatch(db)
      batch.set(newOrderRef, orderData)

      for (const item of bucketItems) {
        const rollDocRef = doc(db, "rollStore", item.id)
        // 4. Update each roll with the destination machine
        batch.update(rollDocRef, {
          inOrder: true,
          orderId: newOrderRef.id,
          orderNumber: nextOrderNumber,
          fullOrderNumber: fullOrderNumber,
          status: "Ordered",
          destinationMachine: destinationMachine,
        })
      }

      await batch.commit()
      message.success(
        `Order ${fullOrderNumber} (${bucketItems.length} rolls) has been placed successfully!`,
      )
      setBucketItems([])
      setIsBucketVisible(false)
    } catch (error) {
      console.error("Error placing order: ", error)
      message.error("Failed to place order. Please try again.")
    } finally {
      setIsPlacingOrder(false)
    }
  }

  const toggleBucketVisibility = () => {
    setIsBucketVisible((prev) => !prev)
  }

  // ... other functions (getcolumnSearchProps, etc.) remain the same ...
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm()
  }
  const handleReset = (clearFilters, confirm) => {
    clearFilters()
    confirm()
  }
  const handleRangeFilter = (dataIndex, values, confirm) => {
    confirm()
  }
  const handleRangeReset = (dataIndex, clearFilters, confirm) => {
    clearFilters()
    confirm()
  }

  const getColumnSearchProps = (dataIndex, columnTitle) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInputRef}
          placeholder={`Search ${columnTitle}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}>
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      const keys = Array.isArray(dataIndex) ? dataIndex : [dataIndex]
      let data = record
      for (let key of keys) {
        if (data && typeof data === "object" && key in data) {
          data = data[key]
        } else {
          data = undefined
          break
        }
      }
      return data ? data.toString().toLowerCase().includes(value.toLowerCase()) : false
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInputRef.current?.select(), 100)
      }
    },
  })
  const getColumnNumberRangeFilterProps = (dataIndex, columnTitle) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
      const currentRange = selectedKeys[0] || [null, null]
      return (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Space direction="vertical">
            <Input.Group compact>
              <InputNumber
                style={{ width: 100, textAlign: "center" }}
                placeholder="Min"
                value={currentRange[0]}
                onChange={(value) =>
                  setSelectedKeys(
                    value !== null || currentRange[1] !== null ? [[value, currentRange[1]]] : [],
                  )
                }
              />
              <Input
                className="site-input-split"
                style={{ width: 30, borderLeft: 0, borderRight: 0, pointerEvents: "none" }}
                placeholder="~"
                disabled
              />
              <InputNumber
                style={{ width: 100, textAlign: "center" }}
                placeholder="Max"
                value={currentRange[1]}
                onChange={(value) =>
                  setSelectedKeys(
                    currentRange[0] !== null || value !== null ? [[currentRange[0], value]] : [],
                  )
                }
              />
            </Input.Group>
            <Space>
              <Button
                type="primary"
                onClick={() => handleRangeFilter(dataIndex, selectedKeys[0], confirm)}
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90 }}
                disabled={
                  !selectedKeys[0] || (selectedKeys[0][0] === null && selectedKeys[0][1] === null)
                }>
                Filter
              </Button>
              <Button
                onClick={() => handleRangeReset(dataIndex, clearFilters, confirm)}
                size="small"
                style={{ width: 90 }}>
                Reset
              </Button>
            </Space>
          </Space>
        </div>
      )
    },
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      const recordValue = record[dataIndex]
      if (recordValue === null || recordValue === undefined) return false
      const [min, max] = value
      if (min === null && max === null) return true
      if (min !== null && max !== null) return recordValue >= min && recordValue <= max
      if (min !== null) return recordValue >= min
      if (max !== null) return recordValue <= max
      return true
    },
  })
  const materialFilters = useMemo(() => {
    if (rollsLoading || !rollsData.length) return []
    const uniqueMaterials = [
      ...new Set(rollsData.map((item) => item.material?.material).filter(Boolean)),
    ]
    return uniqueMaterials.map((materialName) => ({
      text: materialName,
      value: materialName,
    }))
  }, [rollsData, rollsLoading])

  const orderFilters = useMemo(() => {
    const allNums = rollsData
      .map((r) => r.fullOrderNumber)
      .filter((num) => typeof num === "string" && num.length > 0)
    const uniqueNums = [...new Set(allNums)].sort()
    return uniqueNums.map((ord) => ({
      text: ord,
      value: ord,
    }))
  }, [rollsData])

  const statusColumnFilters = useMemo(() => {
    const staticFilters = [
      { text: "In Stock", value: "FILTER_STATUS_IN_STOCK" },
      { text: "Arriving in Stock", value: "FILTER_STATUS_ARRIVING" },
      { text: "Shipping", value: "FILTER_STATUS_SHIPPING" },
    ]
    const dynamicOrderFilters = orderFilters.map((of) => ({
      text: `Order: ${of.text}`,
      value: of.value,
    }))
    return [...staticFilters, ...dynamicOrderFilters]
  }, [orderFilters])

  // 1) Build pallet‐location filters
  const palletLocationFilters = useMemo(() => {
    // grab every location (truthy only) and dedupe
    const allLocations = rollsData
      .map((r) => r.palletLocation)
      .filter((loc) => loc !== undefined && loc !== null && loc !== "")
    const unique = Array.from(new Set(allLocations))
    return unique.map((loc) => ({ text: loc, value: loc }))
  }, [rollsData])

  const rollsColumns = [
    {
      title: "Status",
      key: "rollStatus",
      align: "left",
      width: 200,
      fixed: "left",
      render: (text, record) => {
        if (record.shipping) {
          return (
            <Tag icon={<CarOutlined />} color="#eb2f96">
              Shipping
            </Tag>
          )
        }
        if (!record.verified) {
          return (
            <Tag icon={<ClockCircleOutlined />} color="processing">
              Arriving in Stock
            </Tag>
          )
        }
        if (record.inOrder) {
          return (
            <Tag icon={<ExclamationCircleOutlined />} color="warning">
              In Order ({record.fullOrderNumber || `Order ID #${record.orderId?.slice(-4)}`})
            </Tag>
          )
        }
        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            In Stock
          </Tag>
        )
      },
      filters: statusColumnFilters,
      onFilter: (value, record) => {
        switch (value) {
          case "FILTER_STATUS_IN_STOCK":
            return record.verified && !record.inOrder
          case "FILTER_STATUS_ARRIVING":
            return !record.verified
          default:
            return record.inOrder && record.fullOrderNumber === value
        }
      },
      filterSearch: true,
    },
    {
      title: "Location",
      dataIndex: "palletLocation",
      align: "center",
      width: 120,

      filters: palletLocationFilters, // ← add filters here
      onFilter: (value, record) => record.palletLocation === value,
      filterSearch: true, // ← optional search within dropdown
    },
    {
      title: "Material",
      dataIndex: ["material", "material"],
      align: "center",

      filters: materialFilters,
      onFilter: (value, record) => record.material?.material === value,
      filterSearch: true,
    },
    {
      title: "Barcode",
      dataIndex: "barcode",
      align: "center",
      ...getColumnSearchProps("barcode", "Barcode"),
    },

    {
      title: "Width",
      dataIndex: "rollWidth",
      align: "left",
      width: 120,
      sorter: (a, b) => a.rollWidth - b.rollWidth,
      ...getColumnNumberRangeFilterProps("rollWidth", "Width (mm)"),
    },
    {
      title: "Meters",
      dataIndex: "remainingMeters",
      align: "left",
      width: 120,
      sorter: (a, b) => a.remainingMeters - b.remainingMeters,
      ...getColumnNumberRangeFilterProps("remainingMeters", "Meters"),
    },
    {
      title: "Price (€)",
      dataIndex: "price",
      align: "left",
      width: 120,
      render: (price) => {
        if (typeof price === "number") {
          return `€${price.toFixed(2)}`
        }
        return "N/A"
      },
      sorter: (a, b) => (a.price || 0) - (b.price || 0),
      ...getColumnNumberRangeFilterProps("price", "Price"),
    },
    {
      title: "Action",
      align: "center",
      key: "action",
      fixed: "right",
      width: 180,
      render: (text, record) => {
        const isInBucket = bucketItems.some((item) => item.id === record.id)
        const isActionable = record.verified && !record.inOrder
        if (isInBucket) {
          return (
            <Button
              type="default"
              danger
              onClick={() => handleRemoveFromBucket(record.id)}
              icon={<DeleteOutlined />}
              size="small">
              Remove
            </Button>
          )
        } else {
          return (
            <Button
              type="primary"
              onClick={() => handleAddToBucket(record)}
              icon={<ShoppingCartOutlined />}
              size="small"
              disabled={!isActionable}>
              Add to Order
            </Button>
          )
        }
      },
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader icon={<CopyrightCircleOutlined />} text="Available Rolls for Order" />
        </Col>
        <Col span={24} style={{ display: "flex", justifyContent: "flex-start", marginBottom: 16 }}>
          <Space align="center" size="large">
            <Badge count={bucketItems.length}>
              <Button
                type="default"
                onClick={toggleBucketVisibility}
                icon={<ShoppingCartOutlined />}
                size="middle">
                View Order Bucket
              </Button>
            </Badge>
            {bucketTotalSum > 0 && (
              <Typography.Title level={5} style={{ margin: 0 }}>
                Total: €{bucketTotalSum.toFixed(2)}
              </Typography.Title>
            )}
          </Space>
        </Col>
        <Col span={24}>
          <Table
            dataSource={rollsData}
            loading={rollsLoading}
            rowKey={(record) => record.id}
            columns={rollsColumns}
            size="small"
            pagination={false}
            scroll={{ y: "calc(100vh - 320px)" }}
          />
        </Col>
      </Row>

      {/* 5. Pass machines data down to the drawer */}
      <OrderBucketDrawer
        visible={isBucketVisible}
        onClose={toggleBucketVisibility}
        items={bucketItems}
        onRemoveItem={handleRemoveFromBucket}
        onPlaceOrder={handlePlaceOrder}
        isPlacingOrder={isPlacingOrder}
        machines={machinesData}
        machinesLoading={machinesLoading}
      />
    </div>
  )
}
